import { isNil, noop, throttle } from 'lodash-es';
import { ValueWithBackup } from './utils';
export class ArrangeableList {
    static create(options) {
        var _a;
        return new ArrangeableList(options.manipulator, options.inspector, (_a = options.multiLevel) !== null && _a !== void 0 ? _a : true, options.onItemOver, options.onStartRearrangement, options.onRearrangementFinished);
    }
    static isUpdatable(node) {
        return (!isNil(node) && typeof node.requestUpdate === 'function');
    }
    refresh() {
        this.unregister();
        this.register();
    }
    register() {
        const handles = this.inspector.getRearrangingHandles(this.inspector.getRootContainer());
        handles.forEach(item => {
            item.draggable = true;
            item.addEventListener('dragstart', this.dragStart);
            item.addEventListener('dragend', this.dragEnd);
        });
        const items = this.inspector.getChildElements(this.inspector.getRootContainer());
        items.forEach(item => {
            item.addEventListener('dragover', this.dragOver);
            item.addEventListener('dragenter', this.dragEnter);
            item.addEventListener('dragleave', this.dragLeave);
        });
    }
    unregister() {
        const handles = this.inspector.getRearrangingHandles(this.inspector.getRootContainer());
        handles.forEach(item => {
            item.draggable = false;
            item.removeEventListener('dragstart', this.dragStart);
            item.removeEventListener('dragend', this.dragEnd);
        });
        const items = this.inspector.getChildElements(this.inspector.getRootContainer());
        items.forEach(item => {
            item.removeEventListener('dragover', this.dragOver);
            item.removeEventListener('dragenter', this.dragEnter);
            item.removeEventListener('dragleave', this.dragLeave);
        });
    }
    constructor(manipulator, inspector, multiLevel, onItemOver = noop, onStartRearrangement = noop, onRearrangementFinished = noop) {
        this.manipulator = manipulator;
        this.inspector = inspector;
        this.multiLevel = multiLevel;
        this.onItemOver = onItemOver;
        this.onStartRearrangement = onStartRearrangement;
        this.onRearrangementFinished = onRearrangementFinished;
        this.activeItem = new ValueWithBackup();
        this.dragClone = new ValueWithBackup();
        this.dragStart = (e) => {
            this.removeDragGhost(e);
            this.setDropEffect(e);
            const targetElement = this.inspector.getClosestElement(e.target);
            if (!isNil(targetElement)) {
                this.createDragClone(e).then(c => {
                    this.onStartRearrangement(targetElement, c);
                }, e => console.error(e));
                const container = this.inspector.getRootContainer();
                this.markContainerAsRearranging(container);
                this.markTargetAsRearranging(targetElement);
                this.startRearrangement(targetElement);
            }
            else {
                console.error('cannot find element near drag target');
            }
        };
        this.dragEnter = (e) => {
            e.preventDefault();
            this.setDropEffect(e, this.activeItem.get());
        };
        this.dragLeave = (e) => {
            e.preventDefault();
            const targetElement = this.inspector.findAllElementsByData('insertion-point');
            if (!isNil(targetElement)) {
                this.unmarkInsertionPoints(targetElement);
            }
            else {
                console.error('cannot find element near drag leave target');
            }
        };
        this.dragOver = (e) => {
            var _a;
            e.preventDefault();
            this.setDropEffect(e, this.activeItem.get());
            (_a = this.rearrange(e)) === null || _a === void 0 ? void 0 : _a.catch(e => console.error(e));
        };
        this.dragEnd = (e) => {
            e.preventDefault();
            this.removeDragClone().catch(e => console.error(e));
            const activeItem = this.activeItem.get();
            if (!isNil(activeItem)) {
                this.activeItem.remove();
                this.dragClone.remove();
                const container = this.inspector.getRootContainer();
                this.unmarkContainerAsRearranging(container);
                this.unmarkTargetAsRearranging(activeItem);
                this.finishRearrangement(activeItem).catch(e => console.error(e));
            }
        };
        this.rearrange = throttle(async (e) => {
            const dragging = this.activeItem.getOrBackup();
            const dragClone = this.dragClone.getOrBackup();
            const targetElement = this.inspector.getClosestElement(e.target);
            this.moveDragClone(e, dragClone);
            if (!isNil(dragging) &&
                !isNil(dragClone) &&
                !isNil(targetElement) &&
                !this.inspector.isDescendant(targetElement, dragging)) {
                const overItem = targetElement;
                this.scrollWhenAtTheLimit(dragClone, e.clientY);
                await this.restyleOnItemOver(overItem, dragging, e.clientY);
            }
            else if (isNil(dragging)) {
                console.error('activeItem is undefined');
            }
            else if (isNil(targetElement)) {
                console.error('event target is undefined');
            }
            else if (isNil(this.dragClone)) {
                console.error('dragClone is undefined');
            }
        }, 16, { leading: false, trailing: true });
    }
    markContainerAsRearranging(container) {
        container.dataset.rearranging = 'true';
    }
    unmarkContainerAsRearranging(container) {
        delete container.dataset.rearranging;
    }
    markTargetAsRearranging(target) {
        target.dataset.rearrangingItem = 'true';
    }
    unmarkTargetAsRearranging(target) {
        delete target.dataset.rearrangingItem;
    }
    scrollWhenAtTheLimit(ghost, mouseY) {
        const scrollPadding = Math.max(ghost.clientHeight, 100);
        const window = this.inspector.getWindow();
        if (window.innerHeight - mouseY < scrollPadding) {
            window.scroll({
                top: window.scrollY + Math.max(scrollPadding / 2, 40),
                behavior: 'smooth',
            });
        }
        else if (mouseY < scrollPadding) {
            window.scroll({
                top: window.scrollY - Math.max(scrollPadding / 2, 40),
                behavior: 'smooth',
            });
        }
    }
    startRearrangement(activeItem) {
        this.activeItem.setAndBackup(activeItem);
    }
    get isRearranging() {
        const container = this.inspector.getRootContainer();
        return container.dataset.rearranging === 'true';
    }
    async rearrangeOnFinish(activeItem, insertionPoint, position) {
        if (position === 'before') {
            return this.moveBefore(activeItem, insertionPoint);
        }
        else if (position === 'after') {
            return this.moveAfter(activeItem, insertionPoint);
        }
        else if (position === 'inside') {
            return this.moveInside(activeItem, insertionPoint);
        }
        else {
            console.error('Invalid insertion point %s', position);
        }
    }
    async restyleOnItemOver(overItem, activeItem, mouseY) {
        this.onItemOver(overItem, activeItem);
        await this.findAndMarkInsertionPoint(overItem, this.inspector.getClosestContainer(overItem), mouseY);
    }
    async findAndMarkInsertionPoint(overItem, node, mouseY) {
        if (await this.shouldMoveBefore(mouseY, overItem)) {
            this.markInsertionPoint(node, 'before');
        }
        else if (await this.shouldMoveAfter(mouseY, overItem)) {
            this.markInsertionPoint(node, 'after');
        }
        else if (await this.shouldMoveInside(mouseY, overItem)) {
            this.markInsertionPoint(node, 'inside');
        }
    }
    async finishRearrangement(activeItem) {
        const insertionPoint = this.inspector.findElementByData('insertion-point');
        const position = insertionPoint === null || insertionPoint === void 0 ? void 0 : insertionPoint.dataset.insertionPoint;
        if (!isNil(insertionPoint) && !isNil(position)) {
            this.unmarkInsertionPoints(this.inspector.findAllElementsByData('insertion-point'));
            await this.rearrangeOnFinish(activeItem, insertionPoint, position);
            this.onRearrangementFinished(activeItem);
            this.updateNode(this.inspector.getRootContainer());
        }
        else if (isNil(insertionPoint)) {
            console.debug('insertion point is not specified, no rearrangement');
        }
        else if (isNil(position)) {
            console.error('insertionPoint position is undefined');
        }
    }
    updateNode(node) {
        if (ArrangeableList.isUpdatable(node)) {
            node.requestUpdate();
        }
        this.updateChildNodes(node);
    }
    updateChildNodes(node) {
        if (!isNil(node)) {
            for (const child of this.inspector.getChildElements(node)) {
                if (ArrangeableList.isUpdatable(child)) {
                    child.requestUpdate();
                }
            }
        }
    }
    removeDragGhost(e) {
        var _a;
        const transparentImg = this.manipulator.createImage('data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7');
        (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.setDragImage(transparentImg, 0, 0);
    }
    setDropEffect(e, activeItem) {
        const dataTransfer = e.dataTransfer;
        const targetElement = this.inspector.getClosestElement(e.target);
        if (!isNil(dataTransfer) &&
            (isNil(activeItem) ||
                (!isNil(targetElement) && !this.inspector.isDescendant(targetElement, activeItem)))) {
            dataTransfer.dropEffect = 'move';
        }
        else if (!isNil(dataTransfer)) {
            dataTransfer.dropEffect = 'none';
        }
    }
    async createDragClone(e) {
        var _a;
        const dragging = this.inspector.getClosestElement(e.target);
        const existingClone = (_a = this.inspector.findElementByData('id', 'drag-clone')) !== null && _a !== void 0 ? _a : this.dragClone.getOrBackup();
        if (isNil(existingClone) && !isNil(dragging)) {
            const clone = this.manipulator.cloneElement(dragging);
            this.dragClone.setAndBackup(clone);
            this.dragStartY = e.clientY;
            this.dragStartX = e.clientX;
            const rootContainer = this.inspector.getRootContainer();
            const parentRect = await this.inspector.getBoundingRect(rootContainer);
            this.dragCloneInitialTop = e.y - parentRect.y;
            this.dragCloneInitialLeft = e.x - parentRect.x;
            this.initialScrollTop = this.inspector.getWindow().scrollY;
            this.initialScrollLeft = this.inspector.getWindow().scrollX;
            clone.style.top = `${this.dragCloneInitialTop}px`;
            clone.style.left = `${this.dragCloneInitialLeft}px`;
            clone.dataset.id = 'drag-clone';
            await this.manipulator.insert(clone, 
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.inspector.getFirstChildElement(rootContainer), 'before');
            return clone;
        }
        else if (isNil(existingClone)) {
            throw new Error('cannot find list element near drag target');
        }
        else {
            return existingClone;
        }
    }
    moveDragClone(e, clone) {
        if (!isNil(clone) &&
            !isNil(this.dragStartY) &&
            !isNil(this.dragStartX) &&
            !isNil(this.dragCloneInitialTop) &&
            !isNil(this.dragCloneInitialLeft) &&
            !isNil(this.initialScrollTop) &&
            !isNil(this.initialScrollLeft)) {
            const window = this.inspector.getWindow();
            const scrollOffsetY = window.scrollY - this.initialScrollTop;
            const scrollOffsetX = window.scrollX - this.initialScrollLeft;
            const mouseMoveY = e.clientY - this.dragStartY;
            const mouseMoveX = e.clientX - this.dragStartX;
            clone.style.top = `${this.dragCloneInitialTop + mouseMoveY + scrollOffsetY}px`;
            clone.style.left = `${this.dragCloneInitialLeft + mouseMoveX - scrollOffsetX}px`;
        }
    }
    findAfterInsertionPoint(elem) {
        const insertionPoint = this.inspector.getFirstChildElement(elem);
        if (!isNil(insertionPoint)) {
            return {
                elem: insertionPoint,
                position: 'before',
            };
        }
        else {
            return {
                elem: elem,
                position: 'after',
            };
        }
    }
    findInsideInsertionPoint(elem) {
        const child = this.inspector.getFirstChildElement(elem);
        if (!isNil(child)) {
            return {
                elem: child,
                position: 'before',
            };
        }
        else {
            return {
                elem: elem,
                position: 'inside',
            };
        }
    }
    findInsertionPoint(elem, position) {
        switch (position) {
            case 'before':
                return {
                    elem,
                    position: 'before',
                };
            case 'after':
                return this.findAfterInsertionPoint(elem);
            case 'inside':
                return this.findInsideInsertionPoint(elem);
        }
    }
    markInsertionPoint(sibling, requestedPosition) {
        const { position: actualPosition, elem } = this.findInsertionPoint(sibling, requestedPosition);
        if (!isNil(elem)) {
            this.markFoundInsertionPoint(elem, actualPosition);
        }
    }
    unmarkInsertionPoints(insertionPoints) {
        insertionPoints.forEach(oldPoint => {
            this.unmarkInsertionPoint(oldPoint);
        });
    }
    unmarkInsertionPoint(insertionPoint) {
        delete insertionPoint.dataset.insertionPoint;
    }
    markFoundInsertionPoint(insertionPoint, position) {
        if (this.isRearranging) {
            const insertionPoints = this.inspector.findAllElementsByData('insertion-point');
            this.unmarkInsertionPoints(insertionPoints);
            insertionPoint.dataset.insertionPoint = position;
        }
    }
    async removeDragClone() {
        const clone = this.dragClone.getOrBackup();
        if (!isNil(clone)) {
            await this.manipulator.remove(clone);
            this.dragClone.clean();
        }
    }
    async moveBefore(dragging, node) {
        await this.manipulator.insert(dragging, node, 'before');
        this.updateNode(dragging.parentElement);
    }
    async moveAfter(dragging, node) {
        await this.manipulator.insert(dragging, node, 'after');
        this.updateNode(dragging.parentElement);
    }
    async moveInside(dragging, node) {
        await this.manipulator.insert(dragging, node, 'inside');
        this.updateNode(dragging.parentElement);
    }
    getInsertionFraction() {
        return this.multiLevel ? 4 : 2;
    }
    async shouldMoveBefore(mouseY, dragover) {
        const dragoverRect = await this.inspector.getBoundingRect(dragover);
        return mouseY < dragoverRect.top + dragoverRect.height / this.getInsertionFraction();
    }
    async shouldMoveAfter(mouseY, dragover) {
        const dragoverRect = await this.inspector.getBoundingRect(dragover);
        return mouseY > dragoverRect.bottom - dragoverRect.height / this.getInsertionFraction();
    }
    async shouldMoveInside(mouseY, dragover) {
        const dragoverRect = await this.inspector.getBoundingRect(dragover);
        if (this.multiLevel) {
            return (mouseY > dragoverRect.top + dragoverRect.height / this.getInsertionFraction() &&
                mouseY < dragoverRect.bottom - dragoverRect.height / this.getInsertionFraction());
        }
        else {
            return false;
        }
    }
}
